import { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { fetchTouristPanelContent, updateTouristPanelContent } from "@/lib/touristPanelApi";
import { useCurrentUser } from "@/lib/auth";
import { canWriteStammdaten } from "@/lib/permissions";
import { StammdatenWriteHint } from "@/pages/stammdaten/StammdatenWriteHint";
import type { TouristPanelContent } from "@/types/touristPanel";

export function TouristPanelPagesPanel() {
  const { user } = useCurrentUser();
  const groups = useMemo(() => (user?.groups as string[] | undefined) ?? [], [user]);
  const canEdit = canWriteStammdaten(groups);

  const [content, setContent] = useState<TouristPanelContent | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchTouristPanelContent();
      setContent(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Laden fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function onSave() {
    if (!canEdit || !content) return;
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      await updateTouristPanelContent({
        pages: content.pages.map((p) => ({
          id: p.id,
          name: p.name,
          enabled: p.enabled,
          sort_order: p.sort_order,
        })),
      });
      setSuccess(true);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  }

  function togglePage(pageId: string, enabled: boolean) {
    if (!content) return;
    setContent({
      ...content,
      pages: content.pages.map((p) => (p.id === pageId ? { ...p, enabled } : p)),
    });
    setSuccess(false);
  }

  if (loading) {
    return <p className="text-sm text-label-secondary">Seiten werden geladen…</p>;
  }

  return (
    <div className="space-y-6">
      {!canEdit ? <StammdatenWriteHint /> : null}

      <Card
        title="Panel-Seiten"
        description="Legen Sie fest, welche Seiten freigeschaltete Tablets anzeigen dürfen."
        bodyClassName="space-y-4"
      >
        <ul className="divide-y divide-separator rounded-lg border border-separator">
          {(content?.pages ?? []).map((page) => (
            <li key={page.id} className="flex items-center justify-between gap-4 px-4 py-3">
              <div>
                <p className="font-medium text-label">{page.name}</p>
                <p className="text-xs text-label-secondary">ID: {page.id}</p>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={page.enabled}
                  disabled={!canEdit}
                  onChange={(e) => togglePage(page.id, e.target.checked)}
                />
                Freigeschaltet
              </label>
            </li>
          ))}
        </ul>
      </Card>

      {error ? (
        <p className="text-sm text-red-600" role="alert">
          {error}
        </p>
      ) : null}
      {success ? <p className="text-sm text-status-green">Gespeichert.</p> : null}

      {canEdit ? (
        <Button type="button" disabled={saving} onClick={() => void onSave()}>
          {saving ? "Speichern…" : "Seiten speichern"}
        </Button>
      ) : null}
    </div>
  );
}
